import { memo, useEffect, useRef } from 'react'
import type { Dispatch, SetStateAction } from 'react'
import { useFrame } from '@react-three/fiber'
import { AnimationMixer as ThreeAnimationMixer, AnimationAction, AnimationClip, Group, LoopOnce } from 'three'
import { INIT_ANIMATIONS } from '../utils'

export interface ActiveAnimations {
  animationsList: string[]
  callback: () => void
}

interface AnimationMixerProps {
  scene: Group
  animations: AnimationClip[]
  activeAnimations: ActiveAnimations
  setIsCurrentlyPlayingAnimations: Dispatch<SetStateAction<boolean>>
}

function AnimationMixer({ scene, animations, activeAnimations, setIsCurrentlyPlayingAnimations }: AnimationMixerProps) {
  const mixerRef = useRef<ThreeAnimationMixer | null>(null)

  // Create the mixer once per scene. Keeping it alive means clips that clamp on
  // their final frame (e.g. INIT_ANIMATIONS opening the doors/windows) hold that
  // pose across later triggers instead of snapping back when a new clip plays.
  useEffect(() => {
    const mixer = new ThreeAnimationMixer(scene)
    mixerRef.current = mixer
    return () => {
      mixer.stopAllAction()
      mixerRef.current = null
    }
  }, [scene])

  // Play the requested clips on the persistent mixer whenever the request changes.
  useEffect(() => {
    const mixer = mixerRef.current
    if (!mixer) return

    const { animationsList, callback } = activeAnimations

    setIsCurrentlyPlayingAnimations(true)

    const finish = () => {
      setIsCurrentlyPlayingAnimations(false)
      callback()
    }

    const resettableActions: AnimationAction[] = []
    let finishedCount = 0
    const total = animationsList.length

    if (total === 0) {
      finish()
      return
    }

    const onFinished = () => {
      finishedCount++
      if (finishedCount >= total) {
        for (const action of resettableActions) {
          action.stop()
          action.reset()
        }
        mixer.update(0)
        finish()
      }
    }

    mixer.addEventListener('finished', onFinished)

    for (const name of animationsList) {
      const clip = animations.find((c) => c.name === name)
      if (!clip) {
        finishedCount++
        if (finishedCount >= total) finish()
        continue
      }
      const action = mixer.clipAction(clip)
      action.setLoop(LoopOnce, 1)
      action.clampWhenFinished = true
      action.reset().play()
      if (!INIT_ANIMATIONS.includes(name)) {
        resettableActions.push(action)
      }
    }

    return () => {
      mixer.removeEventListener('finished', onFinished)
    }
  }, [animations, activeAnimations, setIsCurrentlyPlayingAnimations])

  useFrame((_, delta) => {
    mixerRef.current?.update(delta)
  })

  return null
}

export default memo(AnimationMixer)
